import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
// import axios from 'axios'; // Removed unused import
// Note: axios calls are commented out for security
import { API_ENDPOINTS } from '../config/api';
import { toast } from 'react-toastify';
import supabaseService from '../services/supabaseService';
import { supabase } from '../config/supabase';
import { secureStorage, STORAGE_CONFIG } from '../utils/storage';
import secureLogger from '../services/secureLoggingService';
import { COLLEGE } from '../constants';


// Helper: Read the real Supabase JWT from localStorage.
// Edge Functions (get-students, get-payments, etc.) require a valid
// JWT for admin auth — the anon key won't work.
function getAuthHeaders() {
  try {
    const storageKey = 'sb-jfghvgkvokfphzrfazsm-auth-token';
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const data = JSON.parse(raw);
      if (data?.access_token) {
        return { 'Authorization': `Bearer ${data.access_token}` };
      }
    }
  } catch (e) { /* ignore */ }
  // Fallback to anon key (will fail admin-only Edge Functions)
  return { 'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY || ''}` };
}

const StudentContext = createContext();

export const useStudent = () => {
  const context = useContext(StudentContext);
  if (!context) {
    throw new Error('useStudent must be used within a StudentProvider');
  }
  return context;
};

export const StudentProvider = ({ children }) => {
  const [subjects, setSubjects] = useState([]);
  const [minorSubjects, setMinorSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Set up initial data loading (using Supabase now)
  useEffect(() => {
    // Load initial data from Supabase
      loadInitialData();
  }, []);

  // Helper function to normalize subjects from Supabase JSONB
  const normalizeStudentSubjects = useCallback((student) => {
    const normalized = { ...student };
    
    // Normalize subjects
    if (normalized.subjects) {
      if (typeof normalized.subjects === 'string') {
        try {
          normalized.subjects = JSON.parse(normalized.subjects);
        } catch (e) {
          secureLogger.warn('Failed to parse subjects JSON:', e);
          normalized.subjects = [];
        }
      }
    }
    if (!Array.isArray(normalized.subjects)) {
      normalized.subjects = [];
    }
    
    // Normalize minor_subjects
    if (normalized.minor_subjects) {
      if (typeof normalized.minor_subjects === 'string') {
        try {
          normalized.minor_subjects = JSON.parse(normalized.minor_subjects);
        } catch (e) {
          secureLogger.warn('Failed to parse minor_subjects JSON:', e);
          normalized.minor_subjects = [];
        }
      }
    }
    if (!Array.isArray(normalized.minor_subjects)) {
      normalized.minor_subjects = [];
    }
    
    return normalized;
  }, []);

  // Load initial data from Supabase
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load major subjects from Supabase
      const subjectsData = await supabaseService.getMajorSubjects();
      if (subjectsData.length === 0) {
        // No subjects found - data must be added via admin dashboard
        secureLogger.warn('⚠️ No major subjects found. Please add subjects via admin dashboard.');
        setSubjects([]);
      } else {
        // Map Supabase major_subjects data to expected format (add category field)
        const mappedMajorSubjects = subjectsData.map(subject => ({
              ...subject,
          category: 'major', // All subjects from major_subjects table are major
          has_practical: subject.has_practical === true || subject.has_practical === 1 || subject.has_practical === '1',
          practical_fee: subject.practical_fee || 0,
          base_fee: subject.base_fee || 0
        }));
        
        secureLogger.info('✅ Loaded major subjects from Supabase:', mappedMajorSubjects.length);
        setSubjects(mappedMajorSubjects);
      }

      // Load minor subjects from Supabase
      const minorSubjectsData = await supabaseService.getMinorSubjects();
      if (!minorSubjectsData || minorSubjectsData.length === 0) {
        secureLogger.warn('⚠️ No minor subjects found in database.');
        setMinorSubjects([]);
      } else {
        // Map Supabase data to expected format (add category and practical fields)
        const mappedMinorSubjects = minorSubjectsData.map(subject => ({
          id: subject.id,
          name: subject.name,
          description: subject.description || null,
          category: 'minor', // All subjects from minor_subjects table are minor
            has_practical: 0, // Minor subjects don't have practicals
          practical_fee: 0, // Minor subjects don't have practical fees
          fee: 0, // Minor subjects don't have base fees
          created_at: subject.created_at,
          updated_at: subject.updated_at
        }));
        
        secureLogger.info('✅ Loaded minor subjects from Supabase:', mappedMinorSubjects.length);
        setMinorSubjects(mappedMinorSubjects);
      }

      // Load faculty from Supabase
      const facultyData = await supabaseService.getTeachers();
      if (facultyData.length === 0) {
        // No faculty found - data must be added via admin dashboard
        secureLogger.warn('⚠️ No faculty members found. Please add faculty via admin dashboard.');
        setTeachers([]);
      } else {
        setTeachers(facultyData);
      }

      // Load gallery photos from Supabase
      const galleryData = await supabaseService.getGalleryPhotos();
      if (galleryData.length === 0) {
        // No gallery photos found - data must be added via admin dashboard
        secureLogger.warn('⚠️ No gallery photos found. Please add photos via admin dashboard.');
        setGalleryPhotos([]);
      } else {
        setGalleryPhotos(galleryData);
      }

      // Load students from Edge Function (bypasses RLS, uses service role key)
      try {
        const studentsResponse = await fetch(API_ENDPOINTS.STUDENTS, {
          headers: getAuthHeaders()
        });
        if (studentsResponse.ok) {
          const studentsData = await studentsResponse.json();
          if (studentsData.length === 0) {
            secureLogger.info('ℹ️ No students found in database.');
            setStudents([]);
          } else {
            secureLogger.info('✅ Loaded students from Edge Function:', studentsData.length);
            // Normalize subjects and minor_subjects from JSONB strings to arrays
            const normalizedStudents = studentsData.map(normalizeStudentSubjects);
            setStudents(normalizedStudents);
          }
        } else {
          secureLogger.warn('⚠️ Failed to fetch students from Edge Function:', studentsResponse.status);
          setStudents([]);
        }
      } catch (error) {
        secureLogger.error('❌ Error loading students:', error);
        setStudents([]);
      }

      // Load payments from Edge Function (bypasses RLS, uses service role key)
      try {
        const paymentsResponse = await fetch(API_ENDPOINTS.PAYMENTS, {
          headers: getAuthHeaders()
        });
        if (paymentsResponse.ok) {
          const paymentsData = await paymentsResponse.json();
          if (paymentsData.length === 0) {
            secureLogger.info('ℹ️ No payments found in database.');
            setPayments([]);
          } else {
            secureLogger.info('✅ Loaded payments from Edge Function:', paymentsData.length);
            setPayments(paymentsData);
          }
        } else {
          secureLogger.warn('⚠️ Failed to fetch payments from Edge Function:', paymentsResponse.status);
          setPayments([]);
        }
      } catch (error) {
        secureLogger.error('❌ Error loading payments:', error);
        setPayments([]);
      }

    } catch (error) {
      secureLogger.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load initial data from localStorage (fallback) - SECURITY GATED
  const loadInitialDataFromLocalStorage = useCallback(() => {
    try {
      setLoading(true);
      
      // SECURITY: Check if localStorage fallback is allowed
      if (!STORAGE_CONFIG.allowLocalFallback) {
        secureLogger.security('localStorage fallback disabled in production');
        setSubjects([]);
        setMinorSubjects([]);
        setTeachers([]);
        setGalleryPhotos([]);
        setStudents([]);
        setPayments([]);
        setLoading(false);
        return;
      }

      secureLogger.storage('Loading data from localStorage fallback (DEBUG MODE)');
      
      // Load subjects
      const subjectsData = getSubjects();
      setSubjects(subjectsData);
      
      // Load minor subjects
      const minorSubjectsData = getMinorSubjects();
      setMinorSubjects(minorSubjectsData);
      
      // Load faculty
      const facultyData = getFacultyMembers();
      setTeachers(facultyData);
      
      // Load gallery photos
      const galleryData = getGalleryPhotos();
      setGalleryPhotos(galleryData);
      
      // Load students with security checks
      const studentsData = secureStorage.getItem('students', []);
      secureLogger.info('💾 localStorage students loaded:', studentsData.length);
      setStudents(studentsData);
      
      // Load payments with security checks
      const paymentsData = secureStorage.getItem('payments', []);
      setPayments(paymentsData);
      
      } catch (error) {
        secureLogger.error('Error loading initial data from localStorage:', error);
        // Fallback to empty arrays on error
        setSubjects([]);
        setMinorSubjects([]);
        setTeachers([]);
        setGalleryPhotos([]);
        setStudents([]);
        setPayments([]);
      } finally {
        setLoading(false);
      }
  }, [normalizeStudentSubjects]);

  // Global data synchronization system - Serverless Cross-Computer Sync
  const GLOBAL_STUDENTS_KEY = 'university_students_global_v5';
  const GLOBAL_PAYMENTS_KEY = 'university_payments_global_v5';
  
  // Cross-computer data sharing using URL parameters (automatic, invisible to user)
  const DATA_SYNC_BASE_URL = 'https://gdcbeekapurayodhya.web.app';
  const DATA_SYNC_PARAM = 'data_sync';
  
  // Generate a unique device identifier
  // SECURITY: Removed canvas fingerprinting (privacy-invasive)
  // Use simple session-based ID instead
  const generateDeviceId = () => {
    // Simple session ID without fingerprinting
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  };

  // Store data globally and create automatic sync
  const storeGlobalData = (key, data) => {
    try {
      // SECURITY: Check if localStorage fallback is allowed
      if (!STORAGE_CONFIG.allowLocalFallback) {
        secureLogger.warn('⚠️ SECURITY: localStorage storage disabled in production');
        return false;
      }

      // Store in localStorage with security checks
      secureStorage.setItem(key, data);
      
      // Store in sessionStorage
      sessionStorage.setItem(key, JSON.stringify(data));
      
      // Create a global sync URL (automatic, invisible to user)
      const deviceId = generateDeviceId();
      const timestamp = Date.now();
      const syncData = {
        key,
        data,
        deviceId,
        timestamp,
        version: 'v5'
      };
      
      // Encode the sync data
      // SECURITY: Disabled URL-based data sync (privacy risk)
      // Data may contain sensitive student information
      secureLogger.warn('⚠️ SECURITY: URL-based data sync disabled for privacy protection');
      
      // Store data locally only (no URL sharing)
      secureStorage.setItem(`${key}_local`, syncData);
      
      // Data saved successfully (no notification needed)
      return true;
    } catch (error) {
      secureLogger.error('Error storing global data:', error);
      return false;
    }
  };

  // Check for data sync from URL parameters
  // SECURITY: Disabled URL-based data sync checking (privacy risk)
  const checkForDataSync = () => {
    secureLogger.warn('⚠️ SECURITY: URL-based data sync disabled for privacy protection');
    // No URL sync processing for security
    return null;
  };

  // Retrieve data from multiple sources
  const retrieveGlobalData = (key, defaultValue = []) => {
    try {
      // SECURITY: Check if localStorage fallback is allowed
      if (!STORAGE_CONFIG.allowLocalFallback) {
        secureLogger.warn('⚠️ SECURITY: localStorage retrieval disabled in production');
        return defaultValue;
      }

      // First check for URL sync
      const syncedData = checkForDataSync();
      if (syncedData) {
        return syncedData;
      }
      
      // Try localStorage first with security checks
      let data = secureStorage.getItem(key, null);
      if (data) {
        return data;
      }
      
      // Try sessionStorage
      data = sessionStorage.getItem(key);
      if (data) {
        return JSON.parse(data);
      }
      
      // Return default value if nothing found
      return defaultValue;
    } catch (error) {
      secureLogger.error('Error retrieving global data:', error);
      return defaultValue;
    }
  };

  // Force migration function with clean data
  const forceSubjectMigration = () => {
    secureLogger.info('🔄 FORCING subject migration with clean data...');
    const defaultSubjects = [
      // Major Subjects - Clean names without "0" suffixes
      { id: 1, name: "Mathematics", fee: "0.00", has_practical: 0, practical_fee: "0.00", category: "major" },
      { id: 2, name: "Physics", fee: "0.00", has_practical: 1, practical_fee: "200.00", category: "major" },
      { id: 3, name: "Chemistry", fee: "0.00", has_practical: 1, practical_fee: "180.00", category: "major" },
      { id: 4, name: "Biology", fee: "0.00", has_practical: 1, practical_fee: "150.00", category: "major" },
      { id: 5, name: "Botany", fee: "0.00", has_practical: 1, practical_fee: "240.00", category: "major" },
      { id: 6, name: "Zoology", fee: "0.00", has_practical: 1, practical_fee: "240.00", category: "major" },
      { id: 7, name: "English", fee: "0.00", has_practical: 0, practical_fee: "0.00", category: "major" },
      { id: 8, name: "History", fee: "0.00", has_practical: 0, practical_fee: "0.00", category: "major" },
      { id: 9, name: "Geography", fee: "0.00", has_practical: 0, practical_fee: "0.00", category: "major" },
      { id: 10, name: "Computer Science", fee: "0.00", has_practical: 1, practical_fee: "250.00", category: "major" },
      { id: 11, name: "Hindi", fee: "0.00", has_practical: 0, practical_fee: "0.00", category: "major" },
      { id: 12, name: "Physical Education", fee: "0.00", has_practical: 0, practical_fee: "0.00", category: "major" },
      
      // Minor Subjects - Clean names
      { id: 17, name: "History", fee: "0.00", has_practical: 0, practical_fee: "0.00", category: "minor" },
      { id: 18, name: "Geography", fee: "0.00", has_practical: 0, practical_fee: "0.00", category: "minor" },
      { id: 19, name: "Biology", fee: "0.00", has_practical: 1, practical_fee: "150.00", category: "minor" },
      { id: 20, name: "Political Science", fee: "0.00", has_practical: 0, practical_fee: "0.00", category: "minor" },
      { id: 21, name: "Sociology", fee: "0.00", has_practical: 0, practical_fee: "0.00", category: "minor" },
    ];
    
    localStorage.setItem('subjects', JSON.stringify(defaultSubjects));
    secureLogger.info('✅ FORCED migration completed with clean data!');
    return defaultSubjects;
  };

  // Get subjects from localStorage (fallback only - data should come from API/backend)
  const getSubjects = () => {
    const stored = localStorage.getItem('subjects');
    if (stored) {
      const subjects = JSON.parse(stored);
      // Check if subjects need migration (missing category field)
      const needsMigration = subjects.some(subject => !subject.category);
      
      if (needsMigration) {
        secureLogger.info('🔄 Migrating subjects to include category field...');
        const migratedSubjects = subjects.map(subject => {
          // Assign categories based on subject names
          let category = 'major'; // default
          
          if (['History', 'Geography', 'Political Science', 'Sociology'].includes(subject.name)) {
            category = 'major';
          }
          
          return {
            ...subject,
            category: subject.category || category
          };
        });
        
        // Save migrated subjects back to localStorage
        localStorage.setItem('subjects', JSON.stringify(migratedSubjects));
        secureLogger.info('✅ Subjects migrated successfully!');
        return migratedSubjects;
      }
      
      return subjects;
    }
    
    // Return empty array - data must be loaded from API/backend/database
    secureLogger.warn('⚠️ No subjects found in localStorage. Data should be loaded from API/backend.');
    return [];
  };

  // Get faculty members from localStorage (fallback only - data should come from API/backend)
  const getFacultyMembers = () => {
    const stored = localStorage.getItem('faculty_members');
    if (stored) {
      return JSON.parse(stored);
    }
    // Return empty array - data must be loaded from API/backend/database
    secureLogger.warn('⚠️ No faculty members found in localStorage. Data should be loaded from API/backend.');
    return [];
  };

  // Get gallery photos (data must come from API/backend/database)
  const getGalleryPhotos = () => {
    // Return empty array - data must be loaded from API/backend/database
    secureLogger.warn('⚠️ Gallery photos must be loaded from API/backend/database.');
    return [];
  };

  const saveSubjects = (subjectsList) => {
    // SECURITY: Check if localStorage fallback is allowed
    if (!STORAGE_CONFIG.allowLocalFallback) {
      secureLogger.warn('⚠️ SECURITY: localStorage storage disabled in production');
      return;
    }
    secureStorage.setItem('subjects', subjectsList);
  };

  // Clean up duplicate subjects
  const cleanDuplicateSubjects = () => {
    const currentSubjects = getSubjects();
    const uniqueSubjects = [];
    const seenNames = new Set();
    
    currentSubjects.forEach(subject => {
      const nameKey = subject.name.toLowerCase();
      if (!seenNames.has(nameKey)) {
        seenNames.add(nameKey);
        uniqueSubjects.push(subject);
      } else {
        secureLogger.info('🧹 Removing duplicate subject:', subject.name);
      }
    });
    
    if (uniqueSubjects.length !== currentSubjects.length) {
      secureLogger.info(`🧹 Cleaned up ${currentSubjects.length - uniqueSubjects.length} duplicate subjects`);
      saveSubjects(uniqueSubjects);
      setSubjects(uniqueSubjects);
      return true;
    }
    return false;
  };

  // Refresh subjects from Firebase (optimized)
  const refreshSubjectsFromFirebase = async () => {
    try {
      // Load subjects from Supabase instead of Firebase
      const subjectsData = await supabaseService.getMajorSubjects();
      
      if (subjectsData && subjectsData.length > 0) {
        // Map Supabase data to expected format (add category field, convert boolean to number)
        const mappedSubjects = subjectsData.map(subject => ({
          id: subject.id,
          name: subject.name,
          description: subject.description || null,
          category: 'major', // All subjects from major_subjects table are major
          has_practical: subject.has_practical ? 1 : 0, // Convert boolean to number for compatibility
          practical_fee: parseFloat(subject.practical_fee) || 0,
          fee: 0, // Major subjects don't have base fee (only practical fee)
          created_at: subject.created_at,
          updated_at: subject.updated_at
        }));
        
        secureLogger.info('✅ Loaded major subjects from Supabase:', mappedSubjects.length);
        
        saveSubjects(mappedSubjects);
        setSubjects(mappedSubjects);
        return { success: true, subjects: mappedSubjects };
      } else {
        secureLogger.warn('⚠️ No major subjects found in Supabase');
        setSubjects([]);
        return { success: true, subjects: [] };
      }
    } catch (error) {
      secureLogger.error('❌ Error refreshing subjects from Supabase:', error);
      return { success: false, error: error.message };
    }
  };

  // Clean up duplicate gallery photos
  const cleanDuplicateGalleryPhotos = () => {
    const currentPhotos = galleryPhotos; // Use current state instead of getGalleryPhotos()
    const uniquePhotos = [];
    const seenTitles = new Set();
    
    currentPhotos.forEach(photo => {
      const titleKey = photo.title.toLowerCase();
      if (!seenTitles.has(titleKey)) {
        seenTitles.add(titleKey);
        uniquePhotos.push(photo);
      } else {
        secureLogger.info('🧹 Removing duplicate gallery photo:', photo.title);
      }
    });
    
    if (uniquePhotos.length !== currentPhotos.length) {
      secureLogger.info(`🧹 Cleaned up ${currentPhotos.length - uniquePhotos.length} duplicate gallery photos`);
      setGalleryPhotos(uniquePhotos);
      // Also save to Firebase
      // firebaseService.setGalleryPhotos(uniquePhotos);
      return true;
    }
    return false;
  };

  // Refresh gallery photos from Firebase (optimized)
  const refreshGalleryPhotosFromFirebase = async () => {
    try {
      setLoading(true);
      
      // Load from Supabase instead of Firebase
      const supabasePhotos = await supabaseService.getGalleryPhotos();
      if (supabasePhotos && supabasePhotos.length > 0) {
        setGalleryPhotos(supabasePhotos);
        return { success: true, photos: supabasePhotos };
      }
      
      // Return empty array if Firebase is empty - data must come from API/backend
      setGalleryPhotos([]);
      return { success: true, photos: [] };
      
    } catch (error) {
      secureLogger.error('❌ Error refreshing gallery photos from Firebase:', error);
      // Return empty array on error - data must come from API/backend
      setGalleryPhotos([]);
      return { success: false, error: error.message, photos: [] };
    } finally {
      setLoading(false);
    }
  };

  // Refresh faculty from Firebase
  const refreshFacultyFromFirebase = async () => {
    try {
      secureLogger.info('🔄 Refreshing faculty from Supabase...');
      const facultyData = await supabaseService.getTeachers();
      
      if (facultyData && facultyData.length > 0) {
        // Convert Supabase format to expected format (same as fetchTeachers)
        const convertedTeachers = facultyData.map(teacher => ({
          id: teacher.id,
          name: teacher.name,
          email: teacher.email,
          phone_number: teacher.phone_number || teacher.phone || '',
          department: teacher.department || teacher.subject || 'General',
          designation: teacher.designation || 'Faculty',
          qualification: teacher.qualification || '',
          subject: teacher.subject || '',
          joining_date: teacher.joining_date || (teacher.created_at ? new Date(teacher.created_at).toISOString().split('T')[0] : ''),
          photo_url: teacher.photo_url || '',
          photo: teacher.photo_url || '',
          image_url: teacher.photo_url || ''
        }));
        
        secureLogger.info('✅ Refreshed teachers from Supabase:', convertedTeachers.length);
        setTeachers(convertedTeachers);
        return { success: true, teachers: convertedTeachers };
      } else {
        secureLogger.warn('⚠️ No faculty data available from Supabase.');
        setTeachers([]);
        return { success: false, error: 'No faculty members found' };
      }
    } catch (error) {
      secureLogger.error('❌ Error refreshing faculty from Supabase:', error);
      setTeachers([]);
      return { success: false, error: error.message };
    }
  };

  // Manage subjects (add, edit, delete)
  const addSubject = async (subjectData) => {
    try {
      secureLogger.info('➕ Adding major subject with data:', subjectData);
      
      // Check if it's a major subject (default)
      if (subjectData.category === 'major' || !subjectData.category) {
        const result = await supabaseService.manageMajorSubject({
          name: subjectData.name,
          description: subjectData.description || null,
          has_practical: subjectData.has_practical || false,
          practical_fee: parseFloat(subjectData.practical_fee) || 0
        });
      
        if (result.success) {
          await refreshSubjectsFromFirebase();
          toast.success('Major subject added successfully');
          return { success: true };
        } else {
          toast.error(result.error || 'Failed to add subject');
          return { success: false, error: result.error };
        }
      } else {
        // Minor subject
        const result = await supabaseService.manageMinorSubject({
        name: subjectData.name,
          description: subjectData.description || null
        });
        
        if (result.success) {
          await refreshSubjectsFromFirebase();
          toast.success('Minor subject added successfully');
        return { success: true };
        } else {
          toast.error(result.error || 'Failed to add subject');
          return { success: false, error: result.error };
        }
      }
    } catch (error) {
      secureLogger.error('❌ Error adding subject:', error);
      toast.error('Failed to add subject');
      return { success: false, error: error.message };
    }
  };

  const updateSubject = async (subjectData) => {
    try {
      secureLogger.info('🔄 Updating subject with data:', subjectData);
      
      // Check if it's a major subject
      if (subjectData.category === 'major' || !subjectData.category) {
        const result = await supabaseService.manageMajorSubject({
          id: subjectData.id,
              name: subjectData.name,
          description: subjectData.description || null,
          has_practical: subjectData.has_practical || false,
          practical_fee: parseFloat(subjectData.practical_fee) || 0
        });
        
        if (result.success) {
          await refreshSubjectsFromFirebase();
          toast.success('Major subject updated successfully');
          return { success: true };
        } else {
          toast.error(result.error || 'Failed to update subject');
          return { success: false, error: result.error };
        }
      } else {
        // Minor subject
        const result = await supabaseService.manageMinorSubject({
          id: subjectData.id,
          name: subjectData.name,
          description: subjectData.description || null
        });
        
        if (result.success) {
          await refreshSubjectsFromFirebase();
          toast.success('Minor subject updated successfully');
        return { success: true };
        } else {
          toast.error(result.error || 'Failed to update subject');
          return { success: false, error: result.error };
        }
      }
    } catch (error) {
      secureLogger.error('❌ Error updating subject:', error);
      toast.error('Failed to update subject');
      return { success: false, error: error.message };
    }
  };

  const deleteSubject = async (subjectId) => {
    try {
      // Try to delete from major subjects first
      let result = await supabaseService.deleteMajorSubject(subjectId);
      
      if (!result.success) {
        // If not found in major subjects, try minor subjects
        result = await supabaseService.deleteMinorSubject(subjectId);
      }
      
      if (result.success) {
        await refreshSubjectsFromFirebase();
      toast.success('Subject deleted successfully');
      return { success: true };
      } else {
        toast.error(result.error || 'Failed to delete subject');
        return { success: false, error: result.error };
      }
    } catch (error) {
      secureLogger.error('❌ Error deleting subject:', error);
      toast.error('Failed to delete subject');
      return { success: false, error: error.message };
    }
  };

  // Minor Subjects Management
  const getMinorSubjects = () => {
    const stored = localStorage.getItem('minor_subjects');
    if (stored) {
      const subjects = JSON.parse(stored);
      // Ensure all IDs are numbers for consistency and add category if missing
      return subjects.map(subject => ({
        ...subject,
        id: parseInt(subject.id),
        category: subject.category || 'minor' // Default to minor if category is missing
      }));
    }
    // Default minor subjects
    const defaultMinorSubjects = [
      { id: 1, name: "History", fee: "350.00", has_practical: 0, practical_fee: "0.00", category: "minor" },
      { id: 2, name: "Geography", fee: "350.00", has_practical: 0, practical_fee: "0.00", category: "minor" },
      { id: 3, name: "Political Science", fee: "400.00", has_practical: 0, practical_fee: "0.00", category: "minor" },
      { id: 4, name: "Sociology", fee: "400.00", has_practical: 0, practical_fee: "0.00", category: "minor" }
    ];
    
    // Save default minor subjects to localStorage
    localStorage.setItem('minor_subjects', JSON.stringify(defaultMinorSubjects));
    return defaultMinorSubjects;
  };

  const saveMinorSubjects = (minorSubjectsList) => {
    // SECURITY: Check if localStorage fallback is allowed
    if (!STORAGE_CONFIG.allowLocalFallback) {
      secureLogger.warn('⚠️ SECURITY: localStorage storage disabled in production');
      return;
    }
    setMinorSubjects(minorSubjectsList);
    secureStorage.setItem('minor_subjects', minorSubjectsList);
  };

  const fetchMinorSubjects = useCallback(async () => {
    try {
      setLoading(true);
      // Load minor subjects from Supabase
      const minorSubjectsData = await supabaseService.getMinorSubjects();
      
      if (minorSubjectsData && minorSubjectsData.length > 0) {
        // Map Supabase data to expected format
        const mappedMinorSubjects = minorSubjectsData.map(subject => ({
          id: subject.id,
          name: subject.name,
          description: subject.description || null,
          category: 'minor',
          has_practical: 0,
          practical_fee: 0,
          fee: 0,
          created_at: subject.created_at,
          updated_at: subject.updated_at
          }));
          
        setMinorSubjects(mappedMinorSubjects);
        return { success: true, minorSubjects: mappedMinorSubjects };
        } else {
        secureLogger.warn('⚠️ No minor subjects found in database.');
          setMinorSubjects([]);
          return { success: true, minorSubjects: [] };
      }
    } catch (error) {
      secureLogger.error('Error fetching minor subjects:', error);
      setMinorSubjects([]);
      return { success: false, error: error.message, minorSubjects: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  const addMinorSubject = async (subjectData) => {
    try {
      secureLogger.info('➕ Adding minor subject with data:', subjectData);
      
      const result = await supabaseService.manageMinorSubject({
        name: subjectData.name,
        description: subjectData.description || null
      });
      
      if (result.success) {
        // Refresh minor subjects list
        await fetchMinorSubjects();
      toast.success('Minor subject added successfully');
      return { success: true };
      } else {
        toast.error(result.error || 'Failed to add minor subject');
        return { success: false, error: result.error };
      }
    } catch (error) {
      secureLogger.error('❌ Error adding minor subject:', error);
      toast.error('Failed to add minor subject');
      return { success: false, error: error.message };
    }
  };

  const updateMinorSubject = async (subjectId, subjectData) => {
    try {
      secureLogger.info('🔄 Updating minor subject with data:', subjectData);
      
      const result = await supabaseService.manageMinorSubject({
        id: subjectId,
              name: subjectData.name,
        description: subjectData.description || null
      });
      
      if (result.success) {
        // Refresh minor subjects list
        await fetchMinorSubjects();
      toast.success('Minor subject updated successfully');
      return { success: true };
      } else {
        toast.error(result.error || 'Failed to update minor subject');
        return { success: false, error: result.error };
      }
    } catch (error) {
      secureLogger.error('❌ Error updating minor subject:', error);
      toast.error('Failed to update minor subject');
      return { success: false, error: error.message };
    }
  };

  const deleteMinorSubject = async (subjectId) => {
    try {
      secureLogger.info('🗑️ Deleting minor subject with ID:', subjectId);
      
      const result = await supabaseService.deleteMinorSubject(subjectId);
      
      if (result.success) {
        // Refresh minor subjects list
        await fetchMinorSubjects();
      toast.success('Minor subject deleted successfully');
      return { success: true };
      } else {
        toast.error(result.error || 'Failed to delete minor subject');
        return { success: false, error: result.error };
      }
    } catch (error) {
      secureLogger.error('❌ Error deleting minor subject:', error);
      toast.error('Failed to delete minor subject');
      return { success: false, error: error.message };
    }
  };

  // Update payment status when payment is completed
  const updatePaymentStatus = (studentId, paymentMethod = 'PayPal') => {
    try {
      // SECURITY: Check if localStorage fallback is allowed
      if (!STORAGE_CONFIG.allowLocalFallback) {
        secureLogger.security('localStorage fallback disabled in production');
        return { success: false, error: 'Firebase connection required' };
      }

      const existingPayments = secureStorage.getItem('payments', []);
      const updatedPayments = existingPayments.map(payment => {
        if (payment.student_id === studentId) {
          return {
            ...payment,
            status: 'completed',
            payment_date: new Date().toISOString(),
            payment_method: paymentMethod
          };
        }
        return payment;
      });
      secureStorage.setItem('payments', updatedPayments);
      return { success: true };
    } catch (error) {
      secureLogger.error('Error updating payment status:', error);
      return { success: false };
    }
  };

  // Update student payment status in Supabase via Edge Function
  const updateStudentPaymentStatus = async (studentId, paymentStatus) => {
    try {
      setLoading(true);
      secureLogger.info('🔄 Updating student payment status:', { studentId, paymentStatus });
      
      // Use Edge Function instead of direct Supabase call
      const response = await fetch(API_ENDPOINTS.UPDATE_PAYMENT_STATUS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ studentId, paymentStatus })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Update local state
        setStudents(prevStudents => 
          prevStudents.map(student => 
            student.id === studentId 
              ? { ...student, payment_status: paymentStatus }
              : student
          )
        );
        
        toast.success(`Payment status updated to ${paymentStatus}`);
        return { success: true };
      } else {
        toast.error(result.error || 'Failed to update payment status');
        return { success: false, error: result.error };
      }
    } catch (error) {
      secureLogger.error('Error updating student payment status:', error);
      toast.error('Failed to update payment status');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Admin: update an existing student record via Edge Function
  const updateStudent = useCallback(async (studentId, updates) => {
    try {
      setLoading(true);
      secureLogger.info('📝 Updating student via Edge Function', { studentId, updates });

      const response = await fetch(API_ENDPOINTS.UPDATE_STUDENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ id: studentId, updates })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success || !result.student) {
        throw new Error(result.error || 'Failed to update student');
      }

      const updatedStudent = result.student;

      // Ensure subjects is an array (parse if JSON string from Supabase JSONB)
      if (updatedStudent.subjects && typeof updatedStudent.subjects === 'string') {
        try {
          updatedStudent.subjects = JSON.parse(updatedStudent.subjects);
        } catch (e) {
          secureLogger.warn('Failed to parse subjects JSON:', e);
          updatedStudent.subjects = [];
        }
      }
      if (!Array.isArray(updatedStudent.subjects)) {
        updatedStudent.subjects = [];
      }

      // Ensure minor_subjects is an array (parse if JSON string from Supabase JSONB)
      if (updatedStudent.minor_subjects && typeof updatedStudent.minor_subjects === 'string') {
        try {
          updatedStudent.minor_subjects = JSON.parse(updatedStudent.minor_subjects);
        } catch (e) {
          secureLogger.warn('Failed to parse minor_subjects JSON:', e);
          updatedStudent.minor_subjects = [];
        }
      }
      if (!Array.isArray(updatedStudent.minor_subjects)) {
        updatedStudent.minor_subjects = [];
      }

      // Update students state with latest data
      setStudents(prev =>
        prev.map(s => (s.id === updatedStudent.id ? updatedStudent : s))
      );

      toast.success('Student details updated successfully');
      return { success: true, student: updatedStudent };
    } catch (error) {
      secureLogger.error('❌ Error updating student:', error);
      toast.error('Failed to update student: ' + error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // REMOVED: Hardcoded mock data - All data must be loaded from API/backend/database
  // Data should be fetched dynamically via fetchTeachers(), fetchGalleryPhotos(), etc.
  // REMOVED: All hardcoded mock gallery photos - data must be loaded from API/backend/database

  // Fetch subjects with aggressive corruption cleanup
  const fetchSubjects = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load major subjects from Supabase
      const majorSubjectsData = await supabaseService.getMajorSubjects();
      
      if (majorSubjectsData && majorSubjectsData.length > 0) {
        // Map Supabase data to expected format (add category field)
        const mappedSubjects = majorSubjectsData.map(subject => ({
          ...subject,
          category: 'major', // All subjects from major_subjects table are major
          has_practical: subject.has_practical === true || subject.has_practical === 1 || subject.has_practical === '1',
          practical_fee: parseFloat(subject.practical_fee) || 0,
          base_fee: parseFloat(subject.base_fee) || 0
        }));
        
        setSubjects(mappedSubjects);
        secureLogger.info('✅ Major subjects loaded from Supabase:', mappedSubjects.length);
        return { success: true, subjects: mappedSubjects };
      } else {
        secureLogger.warn('⚠️ No major subjects found in database.');
        setSubjects([]);
        return { success: true, subjects: [] };
      }
    } catch (error) {
      secureLogger.error('❌ Error fetching major subjects:', error);
      setSubjects([]);
      return { success: false, error: error.message, subjects: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  // Calculate fee with comprehensive degree-based system
  const calculateFee = useCallback(async (selectedSubjectIds, caste, gender, degree = 'B.Sc') => {
    try {
      secureLogger.info('🚨 CALCULATE FEE CALLED WITH:');
      secureLogger.info('📝 selectedSubjectIds:', selectedSubjectIds);
      secureLogger.info('📝 selectedSubjectIds type:', typeof selectedSubjectIds);
      secureLogger.info('📝 selectedSubjectIds length:', selectedSubjectIds?.length);
      secureLogger.info('📝 caste:', caste);
      secureLogger.info('📝 gender:', gender);
      secureLogger.info('📝 degree:', degree);
      
      // Fetch degree fee structures from Supabase (same source as admin dashboard)
      let degreeStructures = [];
      try {
        degreeStructures = await supabaseService.getDegreeFeeStructures();
        degreeStructures = degreeStructures || [];
        secureLogger.info('✅ Fetched degree fee structures from Supabase:', degreeStructures);
      } catch (error) {
        secureLogger.error('❌ Error fetching degree fee structures from Supabase:', error);
        throw new Error('Fee structure not found. Please try again.');
      }
      
      if (!degreeStructures.length) {
        throw new Error('Fee structure not found. Please configure degree fees in admin dashboard.');
      }
      
      // Find degree structure (case-insensitive, trim for consistency)
      const normalizedDegree = (degree || '').trim().toLowerCase();
      const degreeStructure = degreeStructures.find(d => 
        (d.degree || '').trim().toLowerCase() === normalizedDegree
      );
      
      if (!degreeStructure) {
        throw new Error(`Degree fee not found for: ${degree}. Please configure it in admin dashboard.`);
      }
      
      // Fetch caste/gender discounts from Supabase ONLY (no cache, no fallback)
      let casteGenderDiscounts = [];
      try {
        casteGenderDiscounts = await supabaseService.getCasteGenderDiscounts();
        secureLogger.info('✅ Fetched caste/gender discounts from Supabase:', casteGenderDiscounts);
        
        if (!casteGenderDiscounts || casteGenderDiscounts.length === 0) {
          secureLogger.warn('⚠️ No discounts found in Supabase database');
        }
      } catch (error) {
        secureLogger.error('❌ Error fetching discounts from Supabase:', error);
        // NO FALLBACK - return empty array, discount will be 0
        casteGenderDiscounts = [];
      }
      
      // Normalize caste and gender for matching
      // Handle special cases: SC, ST, OBC should be uppercase
      const normalizeCaste = (c) => {
        if (!c) return '';
        const upper = c.toUpperCase();
        if (['SC', 'ST', 'OBC'].includes(upper)) return upper;
        return c.charAt(0).toUpperCase() + c.slice(1).toLowerCase();
      };
      
      const normalizedCaste = normalizeCaste(caste);
      // Normalize gender: lowercase for matching (database uses lowercase: "male", "female", or NULL)
      const normalizedGender = gender ? gender.toLowerCase() : '';
      
      secureLogger.info('🔍 Discount matching - Input:', {
        originalCaste: caste,
        originalGender: gender,
        normalizedCaste: normalizedCaste,
        normalizedGender: normalizedGender,
        availableDiscounts: casteGenderDiscounts.map(d => ({
          caste: d.caste,
          gender: d.gender,
          deduction_amount: d.deduction_amount
        }))
      });
      
      // Match discount: prioritize specific gender match, then NULL/empty gender (applies to all)
      let discountStructure = null;
      
      // First try to find exact match (caste + gender)
      if (normalizedGender) {
        discountStructure = casteGenderDiscounts.find(d => {
          const dCaste = d.caste ? d.caste.trim().toLowerCase() : '';
          const dGender = d.gender ? d.gender.trim().toLowerCase() : '';
          return dCaste === normalizedCaste.toLowerCase() && dGender === normalizedGender;
        });
        
        secureLogger.info('🔍 Exact match search result:', {
          found: !!discountStructure,
          match: discountStructure ? {
            caste: discountStructure.caste,
            gender: discountStructure.gender,
            deduction_amount: discountStructure.deduction_amount
          } : null
        });
      }
      
      // If no exact match, try to find caste with NULL/empty gender (applies to all genders)
      if (!discountStructure) {
        discountStructure = casteGenderDiscounts.find(d => {
          const dCaste = d.caste ? d.caste.trim().toLowerCase() : '';
          const dGender = d.gender ? String(d.gender).trim().toLowerCase() : '';
          return dCaste === normalizedCaste.toLowerCase() && (dGender === '' || dGender === 'null' || !d.gender);
        });
        
        secureLogger.info('🔍 NULL gender match search result:', {
          found: !!discountStructure,
          match: discountStructure ? {
            caste: discountStructure.caste,
            gender: discountStructure.gender,
            deduction_amount: discountStructure.deduction_amount
          } : null
        });
      }
      
      // Get discount amount (use deduction_amount from Supabase - this is the source of truth)
      const discountAmount = discountStructure 
        ? parseFloat(discountStructure.deduction_amount || 0)
        : 0;
      
      secureLogger.info('💰 Final discount calculation:', {
        caste: normalizedCaste,
        gender: normalizedGender,
        found: !!discountStructure,
        discountAmount: discountAmount,
        deduction_amount_from_db: discountStructure?.deduction_amount,
        discountData: discountStructure
      });
      
      // SIMPLIFIED FEE CALCULATION AS PER REQUIREMENTS:
      // 1. Start with degree base fee (e.g., BSc = 2035)
      // 2. Apply caste discount to base fee
      // 3. Add ONLY practical fees for subjects that have them
      // 4. NO SUBJECT FEES - only practical fees
      
      // Get available subjects from context or localStorage
      let currentSubjects = subjects.length > 0 ? subjects : getSubjects();
      
      // If still empty, force load subjects
      if (!currentSubjects || currentSubjects.length === 0) {
        secureLogger.info('🔧 FORCING subject data load...');
        currentSubjects = forceSubjectMigration();
      }
      
      secureLogger.info('🔍 SUBJECTS DATA DEBUG:');
      secureLogger.info('📝 subjects from context:', subjects);
      secureLogger.info('📝 subjects length:', subjects.length);
      secureLogger.info('📝 getSubjects() result:', getSubjects());
      secureLogger.info('📝 currentSubjects:', currentSubjects);
      secureLogger.info('📝 currentSubjects length:', currentSubjects.length);
      
      // Calculate practical fees for selected subjects
      let practicalFee = 0;
      
      secureLogger.info('🔍 CHECKING SELECTED SUBJECTS ARRAY:');
      secureLogger.info('📝 selectedSubjectIds exists:', !!selectedSubjectIds);
      secureLogger.info('📝 selectedSubjectIds is array:', Array.isArray(selectedSubjectIds));
      secureLogger.info('📝 selectedSubjectIds length:', selectedSubjectIds?.length);

      if (selectedSubjectIds && selectedSubjectIds.length > 0) {
        secureLogger.info('🔍 DETAILED DEBUGGING:');
        secureLogger.info('📝 Selected subject IDs:', selectedSubjectIds);
        secureLogger.info('📝 Subject IDs type:', selectedSubjectIds.map(id => ({ id, type: typeof id })));
        secureLogger.info('📚 Available subjects count:', currentSubjects.length);
        secureLogger.info('📚 Available subjects:', currentSubjects.map(s => ({ 
          id: s.id, 
          name: s.name, 
          has_practical: s.has_practical, 
          practical_fee: s.practical_fee 
        })));
        
        // Get selected subjects with improved matching
        const selectedSubjects = [];
        selectedSubjectIds.forEach(selectedId => {
          secureLogger.info(`🔍 Looking for subject ID: ${selectedId} (type: ${typeof selectedId})`);
          
          // Try multiple matching strategies
          let matchingSubject = currentSubjects.find(subject => 
            subject.id === selectedId || 
            subject.id === parseInt(selectedId) ||
            selectedId === `minor-${subject.id}` ||
            parseInt(selectedId) === subject.id
          );
          
          if (matchingSubject) {
            selectedSubjects.push(matchingSubject);
            secureLogger.info(`✅ Found match: ${selectedId} -> ${matchingSubject.name} (ID: ${matchingSubject.id})`);
          } else {
            secureLogger.info(`❌ No match found for: ${selectedId}`);
            secureLogger.info(`📚 Available subject IDs: ${currentSubjects.map(s => s.id).join(', ')}`);
          }
        });
        
        secureLogger.info('✅ Selected subjects after detailed matching:', selectedSubjects.map(s => ({ 
          id: s.id, 
          name: s.name, 
          has_practical: s.has_practical,
          practical_fee: s.practical_fee 
        })));
        
        // Calculate practical fees for subjects that have them
        selectedSubjects.forEach(subject => {
          secureLogger.info(`🔍 Checking subject: ${subject.name}, has_practical: ${subject.has_practical}, practical_fee: ${subject.practical_fee}`);
        if (subject.has_practical) {
          const practical = parseFloat(subject.practical_fee || 240);
          practicalFee += practical;
          secureLogger.info(`🔬 Practical fee for ${subject.name}: ${practical}`);
          } else {
            secureLogger.info(`❌ No practical fee for ${subject.name}`);
          }
        });
        
        secureLogger.info(`💰 Total practical fee calculated: ${practicalFee}`);
        
        secureLogger.info(`💰 FINAL practical fee: ${practicalFee}`);
      } else {
        secureLogger.info('❌ No subjects selected or subjects array is empty');
      }
      
      // Apply the simplified calculation:
      // Base Fee - Caste Discount + Practical Fees
      const baseFee = degreeStructure.base_fee;
      const discountedBaseFee = Math.max(0, baseFee - discountAmount);
      const finalAmount = discountedBaseFee + practicalFee;
      
      secureLogger.info('💰 Simplified fee calculation:', {
        degree: degree,
        baseFee: baseFee,
        practicalFee: practicalFee,
        caste: normalizedCaste,
        gender: normalizedGender,
        discountAmount: discountAmount,
        discountedBaseFee: discountedBaseFee,
        finalAmount: finalAmount
      });
      
      // Calculate total_fee (base fee + practical fee BEFORE deduction)
      const totalFee = baseFee + practicalFee;
      
      const result = {
        degree: degree,
        baseFee: baseFee.toFixed(2),
        practicalFee: practicalFee.toFixed(2),
        totalFee: totalFee.toFixed(2), // Base fee + practical fee (before deduction)
        discountedBaseFee: discountedBaseFee.toFixed(2),
        caste: normalizedCaste,
        gender: normalizedGender,
        discountAmount: discountAmount.toFixed(2),
        finalAmount: finalAmount.toFixed(2), // After deduction
        breakdown: {
          degreeBaseFee: baseFee,
          practicalFee: practicalFee,
          totalFee: totalFee, // Base fee + practical fee
          discountedBaseFee: discountedBaseFee,
          discountAmount: discountAmount,
          finalAmount: finalAmount
        }
      };
      
      secureLogger.info('💰 Final simplified fee result:', result);
      return result;
    } catch (error) {
      secureLogger.error('Error calculating simplified fee:', error);
      toast.error('Failed to calculate fee');
      throw error;
    }
  }, [minorSubjects]);

  // Degree Fee Structure Management
  const getDegreeFeeStructures = async () => {
    try {
      const data = await supabaseService.getDegreeFeeStructures();
      return data || [];
    } catch (error) {
      secureLogger.error('Error fetching degree fee structures:', error);
      return [];
    }
  };

  const addDegreeFeeStructure = async (degreeStructure) => {
    try {
      const result = await supabaseService.manageDegreeFeeStructure({
        degree: degreeStructure.degree,
        base_fee: degreeStructure.base_fee
      });
      
      if (result.success) {
      toast.success('Degree fee structure added successfully');
      return { success: true };
      } else {
        toast.error(result.error || 'Failed to add degree fee structure');
        return { success: false, error: result.error };
      }
    } catch (error) {
      secureLogger.error('Error adding degree fee structure:', error);
      toast.error('Failed to add degree fee structure');
      return { success: false, error: error.message };
    }
  };

  const updateDegreeFeeStructure = async (id, degreeStructure) => {
    try {
      const result = await supabaseService.manageDegreeFeeStructure({
        id: id,
        degree: degreeStructure.degree,
        base_fee: degreeStructure.base_fee
      });
      
      if (result.success) {
      toast.success('Degree fee structure updated successfully');
      return { success: true };
      } else {
        toast.error(result.error || 'Failed to update degree fee structure');
        return { success: false, error: result.error };
      }
    } catch (error) {
      secureLogger.error('Error updating degree fee structure:', error);
      toast.error('Failed to update degree fee structure');
      return { success: false, error: error.message };
    }
  };

  const deleteDegreeFeeStructure = async (id) => {
    try {
      const result = await supabaseService.deleteDegreeFeeStructure(id);
      
      if (result.success) {
      toast.success('Degree fee structure deleted successfully');
      return { success: true };
      } else {
        toast.error(result.error || 'Failed to delete degree fee structure');
        return { success: false, error: result.error };
      }
    } catch (error) {
      secureLogger.error('Error deleting degree fee structure:', error);
      toast.error('Failed to delete degree fee structure');
      return { success: false, error: error.message };
    }
  };

  // Caste Gender Discount Management
  const getCasteGenderDiscounts = async () => {
    try {
      const data = await supabaseService.getCasteGenderDiscounts();
      return data || [];
    } catch (error) {
      secureLogger.error('Error fetching caste gender discounts:', error);
      return [];
    }
  };

  const addCasteGenderDiscount = async (discount) => {
    try {
      const result = await supabaseService.manageCasteGenderDiscount({
        caste: discount.caste,
        gender: discount.gender || null,
        deduction_amount: discount.deduction_amount || 0,
        discount_percentage: discount.discount_percentage || 0
      });
      
      if (result.success) {
      toast.success('Caste/Gender discount added successfully');
      return { success: true };
      } else {
        toast.error(result.error || 'Failed to add caste/gender discount');
        return { success: false, error: result.error };
      }
    } catch (error) {
      secureLogger.error('Error adding caste/gender discount:', error);
      toast.error('Failed to add caste/gender discount');
      return { success: false, error: error.message };
    }
  };

  const updateCasteGenderDiscount = async (id, discount) => {
    try {
      const result = await supabaseService.manageCasteGenderDiscount({
        id: id,
        caste: discount.caste,
        gender: discount.gender || null,
        deduction_amount: discount.deduction_amount || discount.discount_amount || 0,
        discount_percentage: discount.discount_percentage || 0
      });
      
      if (result.success) {
      toast.success('Caste/Gender discount updated successfully');
      return { success: true };
      } else {
        toast.error(result.error || 'Failed to update caste/gender discount');
        return { success: false, error: result.error };
      }
    } catch (error) {
      secureLogger.error('Error updating caste/gender discount:', error);
      toast.error('Failed to update caste/gender discount');
      return { success: false, error: error.message };
    }
  };

  const deleteCasteGenderDiscount = async (id) => {
    try {
      const result = await supabaseService.deleteCasteGenderDiscount(id);
      
      if (result.success) {
      toast.success('Caste/Gender discount deleted successfully');
      return { success: true };
      } else {
        toast.error(result.error || 'Failed to delete caste/gender discount');
        return { success: false, error: result.error };
      }
    } catch (error) {
      secureLogger.error('Error deleting caste/gender discount:', error);
      toast.error('Failed to delete caste/gender discount');
      return { success: false, error: error.message };
    }
  };

  // Upload file directly to Supabase Storage (replaces base64 conversion)
  const uploadFileToStorage = async (file, fileName, bucketName = 'student-documents') => {
    try {
      // Compress image if it's an image file
      let fileToUpload = file;
      if (file.type.startsWith('image/')) {
        // Compress image before uploading
        const compressedFile = await compressImage(file);
        fileToUpload = compressedFile;
      }

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(fileName, fileToUpload, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Storage upload error:', error);
        throw error;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(data.path);

      return urlData?.publicUrl || null;
    } catch (error) {
      console.error('Error uploading file to storage:', error);
      throw error;
    }
  };

  // Compress image file
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate new dimensions (max 800px width/height)
          let { width, height } = img;
          const maxSize = 800;
          
          if (width > height && width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          } else if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            } else {
              resolve(file); // Fallback to original
            }
          }, 'image/jpeg', 0.7); // 70% quality
        };
        img.onerror = () => resolve(file); // Fallback to original
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Convert file to base64 with compression (kept for backward compatibility)
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Compress the image if it's too large
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate new dimensions (max 800px width/height)
          let { width, height } = img;
          const maxSize = 800;
          
          if (width > height && width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          } else if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7); // 70% quality
          
          resolve(compressedDataUrl);
        };
        img.onerror = () => resolve(reader.result); // Fallback to original
        img.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Process student registration
  const processRegistration = useCallback(async (formData) => {
    try {
      setLoading(true);
      
      // Generate unique IDs with year-based serialized registration number
      const currentYear = new Date().getFullYear();
      
      // Get existing students to determine next serial number
      // TODO: Get from Supabase via EC2 API
      const existingStudents = []; // Placeholder - get from API
      const yearStudents = existingStudents.filter(s => 
        s.registration_number && s.registration_number.startsWith(`REG${currentYear}`)
      );
      
      // Get the highest serial number for this year
      let nextSerialNumber = 1;
      if (yearStudents.length > 0) {
        const serialNumbers = yearStudents.map(s => {
          const regNum = s.registration_number;
          const serialPart = regNum.substring(7); // Remove "REG2025" prefix
          return parseInt(serialPart) || 0;
        });
        nextSerialNumber = Math.max(...serialNumbers) + 1;
      }
      
      const studentId = Math.floor(Math.random() * 10000) + 1000;
      // Don't generate registration number on frontend - let backend generate unique one
      // const registrationNumber = `REG${currentYear}${String(nextSerialNumber).padStart(4, '0')}`;
      
      // Get form data
      const majorSubjects = JSON.parse(formData.get('subjects') || '[]');
      const minorSubjects = JSON.parse(formData.get('minorSubjects') || '[]');
      
      // Combine major and minor subjects for processing
      // Minor subjects already come with "minor-" prefix from the form
      const allSubjects = [
        ...majorSubjects,
        ...minorSubjects
      ];
      
      // Extract all form fields (using correct field names from the form)
      const name = formData.get('name');
      const fatherName = formData.get('fathers_name'); // Fixed: was 'father_name'
      const motherName = formData.get('mothers_name'); // Fixed: was 'mother_name'
      const dateOfBirth = formData.get('date_of_birth');
      const gender = formData.get('gender');
      const caste = formData.get('caste');
      const religion = formData.get('religion');
      const physicallyHandicap = formData.get('physically_handicap');
      const annualIncome = formData.get('annual_income');
      const semester = formData.get('semester');
      const mediumOfInstruction = formData.get('medium'); // Fixed: was 'medium_of_instruction'
      const email = formData.get('email');
      const mobile = formData.get('mobile');
      const aadharNumber = formData.get('aadhar'); // Fixed: was 'aadhar_number'
      const village = formData.get('village');
      const postOffice = formData.get('post'); // Fixed: was 'post_office'
      const district = formData.get('district');
      const state = formData.get('state');
      const pinCode = formData.get('pincode'); // Fixed: was 'pin_code'
      const tcMigrationNumber = formData.get('tc_migration'); // Fixed: was 'tc_migration_number'
      
      // Debug: Log all form data
      secureLogger.info('📝 Form Data Extraction:', {
        name, fatherName, motherName, dateOfBirth, gender, caste, religion,
        physicallyHandicap, annualIncome, semester, mediumOfInstruction,
        email, mobile, aadharNumber, village, postOffice, district, state, pinCode, tcMigrationNumber
      });
      
      // Process uploaded files - Upload directly to Supabase Storage
      const tcPhoto = formData.get('tc_photo');
      const signaturePhoto = formData.get('signature_photo');
      
      // Generate temporary registration number for file naming (will be updated by Edge Function)
      const tempRegNumber = `TEMP_${Date.now()}`;
      
      // Upload files directly to Supabase Storage
      let tcPhotoUrl = null;
      let signaturePhotoUrl = null;
      
      if (tcPhoto) {
        try {
          secureLogger.info('📤 Uploading TC photo to Supabase Storage...');
          tcPhotoUrl = await uploadFileToStorage(tcPhoto, `tc_${tempRegNumber}.png`, 'student-documents');
          secureLogger.info('✅ TC photo uploaded:', tcPhotoUrl);
        } catch (error) {
          secureLogger.error('❌ TC photo upload failed:', error);
          // Fallback to base64 for backward compatibility
          tcPhotoUrl = await convertFileToBase64(tcPhoto);
        }
      }
      
      if (signaturePhoto) {
        try {
          secureLogger.info('📤 Uploading signature photo to Supabase Storage...');
          signaturePhotoUrl = await uploadFileToStorage(signaturePhoto, `signature_${tempRegNumber}.png`, 'student-documents');
          secureLogger.info('✅ Signature photo uploaded:', signaturePhotoUrl);
        } catch (error) {
          secureLogger.error('❌ Signature photo upload failed:', error);
          // Fallback to base64 for backward compatibility
          signaturePhotoUrl = await convertFileToBase64(signaturePhoto);
        }
      }
      
      const degree = formData.get('degree') || 'B.Sc'; // Get degree from form
      const feeDetails = await calculateFee(allSubjects, caste, gender, degree);
      
      // Prepare student data for Edge Function
      const studentData = {
        id: studentId,
        // Don't send registration_number - let Edge Function generate unique one
        // Personal Information
        name: name || null,
        father_name: fatherName || null,
        mother_name: motherName || null,
        date_of_birth: dateOfBirth || null,
        gender: gender || null,
        caste: caste || null,
        religion: religion || null,
        physically_handicap: physicallyHandicap || null,
        annual_income: annualIncome || null,
        semester: semester || null,
        medium_of_instruction: mediumOfInstruction || null,
        // Contact Information
        email: email || null,
        mobile: mobile || null,
        aadhar_number: aadharNumber || null,
        // Address Information
        village: village || null,
        post_office: postOffice || null,
        district: district || null,
        state: state || null,
        pin_code: pinCode || null,
        // Academic Information
        degree: degree || 'B.Sc',
        subjects: Array.isArray(majorSubjects) ? majorSubjects : (majorSubjects ? [majorSubjects] : []), // Major subjects only (array of IDs)
        minorSubjects: Array.isArray(minorSubjects) ? minorSubjects : (minorSubjects ? [minorSubjects] : []), // Minor subjects only (array of IDs with "minor-" prefix)
        tc_migration_number: tcMigrationNumber || null,
        // Financial Information
        total_fee: feeDetails.totalFee || (parseFloat(feeDetails.baseFee) + parseFloat(feeDetails.practicalFee)), // Base fee + practical fee (before deduction)
        baseFee: feeDetails.baseFee || 0,
        practicalFee: feeDetails.practicalFee || 0,
        final_amount: feeDetails.finalAmount || 0, // After deduction
        paid_amount: 0, // Initially 0
        remaining_amount: feeDetails.finalAmount || 0, // Initially equals final_amount
        // System Information
        registration_date: new Date().toISOString(),
        status: 'registered',
        // Send file URLs if uploaded, otherwise send base64 (for backward compatibility)
        tc_photo_url: tcPhotoUrl,
        signature_photo_url: signaturePhotoUrl,
        // Keep base64 as fallback if URL upload failed
        tc_photo: tcPhotoUrl ? null : (tcPhoto ? await convertFileToBase64(tcPhoto) : null),
        signature_photo: signaturePhotoUrl ? null : (signaturePhoto ? await convertFileToBase64(signaturePhoto) : null),
        device_info: {
          userAgent: navigator.userAgent,
          timestamp: Date.now(),
          location: 'Global'
        }
      };
      
      // Debug: Log final student data
      secureLogger.student('Final Student Data', studentData);
      
      // Send student data to Edge Function
      secureLogger.info('📤 Sending student registration to Supabase Edge Function...');
      const apiResponse = await fetch(API_ENDPOINTS.PROCESS_REGISTRATION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(studentData)
      });
      
      if (!apiResponse.ok) {
        const errorData = await apiResponse.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP error! status: ${apiResponse.status}`);
      }
      
      const apiResult = await apiResponse.json();
      secureLogger.info('✅ Student registered via Edge Function:', apiResult);
      
      if (!apiResult.success) {
        throw new Error(apiResult.error || 'Registration failed');
      }
      
      // If files were uploaded with temp names, rename them with actual registration number
      if (apiResult.registrationNumber && (tcPhotoUrl || signaturePhotoUrl)) {
        // Note: File renaming in storage would require additional Edge Function call
        // For now, files are uploaded with temp names and Edge Function handles renaming
      }
      
      const response = {
        success: true,
        student_id: apiResult.studentId || studentId,
        registration_number: apiResult.registrationNumber,
        final_amount: feeDetails.finalAmount,
        message: 'Registration successful!'
      };
      
      return response;
    } catch (error) {
      const message = error.message || 'Registration failed';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [calculateFee]);

  // Delete student from database
  const deleteStudent = useCallback(async (studentId) => {
    try {
      setLoading(true);
      
      // Delete from Supabase via Edge Function
      // Edge Functions: send ID in URL path or body
      const deleteUrl = API_ENDPOINTS.DELETE_STUDENT.includes('/functions/v1') 
        ? `${API_ENDPOINTS.DELETE_STUDENT}/${studentId}` 
        : `${API_ENDPOINTS.DELETE_STUDENT}/${studentId}`;
      
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ studentId }) // Also send in body for Edge Functions
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete student');
      }
      
      // Update local state
      setStudents(prevStudents => prevStudents.filter(student => student.id !== studentId));
      
      // Also delete associated payments
      setPayments(prevPayments => prevPayments.filter(payment => payment.student_id !== studentId));
      
      toast.success('Student deleted successfully');
      return { success: true };
    } catch (error) {
      secureLogger.error('Error deleting student:', error);
      toast.error('Failed to delete student: ' + error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Download student file (TC, migration certificate, etc.)
  const downloadStudentFile = (student, fileType) => {
    try {
      let fileData = null;
      let fileName = '';
      
      switch (fileType) {
        case 'tc':
          fileData = student.tc_photo;
          fileName = `${student.name}_TC_${student.registration_number}.jpg`;
          break;
        case 'signature':
          fileData = student.signature_photo;
          fileName = `${student.name}_Signature_${student.registration_number}.jpg`;
          break;
        default:
          throw new Error('Invalid file type');
      }
      
      if (!fileData) {
        toast.error('File not found');
        return;
      }
      
      // Create download link
      const link = document.createElement('a');
      link.href = fileData;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('File downloaded successfully');
    } catch (error) {
      secureLogger.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  // Build registration receipt HTML (shared for download and print-by-reg). Uses context subjects/minorSubjects.
  const getRegistrationReceiptHtml = (student) => {
    const regDate = student.registration_date
      ? new Date(student.registration_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })
      : 'Not specified';
    const majorSubjectsList = subjects || [];
    const minorSubjectsList = minorSubjects || [];
    const normalizeId = (id) => (id === null || id === undefined ? '' : String(id).trim());
    let studentMajorIds = [];
    if (student.subjects) {
      if (Array.isArray(student.subjects)) studentMajorIds = student.subjects;
      else if (typeof student.subjects === 'string') {
        try { const p = JSON.parse(student.subjects); studentMajorIds = Array.isArray(p) ? p : []; } catch (_) {}
      }
    }
    let studentMinorIds = [];
    if (student.minor_subjects) {
      if (Array.isArray(student.minor_subjects)) studentMinorIds = student.minor_subjects;
      else if (typeof student.minor_subjects === 'string') {
        try { const p = JSON.parse(student.minor_subjects); studentMinorIds = Array.isArray(p) ? p : []; } catch (_) {}
      }
    }
    const majorSubjectList = studentMajorIds.map(id => {
      const nid = normalizeId(id);
      const s = majorSubjectsList.find(x => normalizeId(x.id) === nid);
      return s ? s.name : `Unknown Major Subject (ID: ${id})`;
    });
    const minorSubjectList = studentMinorIds.map(id => {
      const nid = normalizeId(id);
      const s = minorSubjectsList.find(x => normalizeId(x.id) === nid);
      return s ? s.name : `Unknown Minor Subject (ID: ${id})`;
    });
    const tcUploaded = (student.tc_photo_url || student.tc_photo) ? 'Yes' : 'No';
    const sigUploaded = (student.signature_photo_url || student.signature_photo) ? 'Yes' : 'No';
    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Student Registration Form - ${student.name}</title>
    <style>
        body {
            font-family: 'Arial', sans-serif;
            line-height: 1.6;
            margin: 0;
            padding: 20px;
            background-color: #f8f9fa;
        }
        .container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            padding: 30px;
            border-radius: 10px;
            box-shadow: 0 0 20px rgba(0,0,0,0.1);
        }
        .header {
            text-align: center;
            border-bottom: 3px solid #007bff;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        .header h1 {
            color: #007bff;
            margin: 0;
            font-size: 28px;
        }
        .header h2 {
            color: #6c757d;
            margin: 10px 0 0 0;
            font-size: 18px;
            font-weight: normal;
        }
        .section {
            margin-bottom: 25px;
        }
        .section-title {
            background-color: #007bff;
            color: white;
            padding: 10px 15px;
            margin: 0 0 15px 0;
            border-radius: 5px;
            font-size: 16px;
            font-weight: bold;
        }
        .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin-bottom: 15px;
        }
        .info-grid-three {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 15px;
            margin-bottom: 15px;
        }
        .subjects-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 15px;
        }
        .subject-column {
            background-color: #f8f9fa;
            padding: 15px;
            border-radius: 5px;
            border-left: 4px solid #007bff;
        }
        .subject-column h4 {
            margin: 0 0 10px 0;
            color: #007bff;
            font-size: 16px;
        }
        .subject-column ul {
            margin: 0;
            padding-left: 20px;
        }
        .subject-column li {
            margin-bottom: 5px;
            color: #495057;
        }
        .info-item {
            display: flex;
            flex-direction: column;
        }
        .info-label {
            font-weight: bold;
            color: #495057;
            margin-bottom: 5px;
            font-size: 14px;
        }
        .info-value {
            color: #212529;
            padding: 8px 12px;
            background-color: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            font-size: 14px;
        }
        .subjects-list {
            background-color: #f8f9fa;
            padding: 15px;
            border-radius: 5px;
            border-left: 4px solid #007bff;
        }
        .subjects-list ul {
            margin: 0;
            padding-left: 20px;
        }
        .subjects-list li {
            margin-bottom: 5px;
            color: #495057;
        }
        .amount-section {
            background-color: #e7f3ff;
            padding: 20px;
            border-radius: 8px;
            text-align: center;
            border: 2px solid #007bff;
        }
        .amount-value {
            font-size: 24px;
            font-weight: bold;
            color: #007bff;
            margin: 10px 0;
        }
        .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 2px solid #dee2e6;
            text-align: center;
            color: #6c757d;
            font-size: 12px;
        }
        @media print {
            body { background-color: white; }
            .container { box-shadow: none; }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>STUDENT REGISTRATION FORM</h1>
            <h2>${COLLEGE.FULL_NAME}</h2>
        </div>
        
        <div class="section">
            <div class="section-title">📋 Personal Information</div>
            <div class="info-grid-three">
                <div class="info-item">
                    <div class="info-label">Registration Number</div>
                    <div class="info-value">${student.registration_number}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Full Name</div>
                    <div class="info-value">${student.name || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Father's Name</div>
                    <div class="info-value">${student.father_name || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Mother's Name</div>
                    <div class="info-value">${student.mother_name || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Date of Birth</div>
                    <div class="info-value">${student.date_of_birth || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Gender</div>
                    <div class="info-value">${student.gender || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Caste</div>
                    <div class="info-value">${student.caste || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Religion</div>
                    <div class="info-value">${student.religion || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Physically Handicap</div>
                    <div class="info-value">${student.physically_handicap || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Annual Income</div>
                    <div class="info-value">${student.annual_income || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Semester</div>
                    <div class="info-value">${student.semester || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Medium of Instruction</div>
                    <div class="info-value">${student.medium_of_instruction || 'Not provided'}</div>
                </div>
            </div>
        </div>
        
        <div class="section">
            <div class="section-title">📞 Contact Information</div>
            <div class="info-grid">
                <div class="info-item">
                    <div class="info-label">Email Address</div>
                    <div class="info-value">${student.email || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Mobile Number</div>
                    <div class="info-value">${student.mobile || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Aadhar Number</div>
                    <div class="info-value">${student.aadhar_number || 'Not provided'}</div>
                </div>
            </div>
        </div>
        
        <div class="section">
            <div class="section-title">🏠 Address Information</div>
            <div class="info-grid">
                <div class="info-item">
                    <div class="info-label">Village</div>
                    <div class="info-value">${student.village || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Post Office</div>
                    <div class="info-value">${student.post_office || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">District</div>
                    <div class="info-value">${student.district || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">State</div>
                    <div class="info-value">${student.state || 'Not provided'}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">PIN Code</div>
                    <div class="info-value">${student.pin_code || 'Not provided'}</div>
                </div>
            </div>
        </div>
        
        <div class="section">
            <div class="section-title">📚 Academic Information</div>
            <div class="subjects-grid">
                <div class="subject-column">
                    <h4>Major Subjects</h4>
                    <ul>
                        ${majorSubjectList.length > 0 ? 
                          majorSubjectList.map(subject => `<li>${subject}</li>`).join('') : 
                          '<li>No major subjects selected</li>'
                        }
                    </ul>
                </div>
                <div class="subject-column">
                    <h4>Minor Subjects</h4>
                    <ul>
                        ${minorSubjectList.length > 0 ? 
                          minorSubjectList.map(subject => `<li>${subject}</li>`).join('') : 
                          '<li>No minor subjects selected</li>'
                        }
                    </ul>
                </div>
            </div>
            <div class="info-grid">
                <div class="info-item">
                    <div class="info-label">TC/Migration Number</div>
                    <div class="info-value">${student.tc_migration_number || 'Not provided'}</div>
                </div>
            </div>
        </div>
        
        <div class="section">
            <div class="section-title">📄 Document Information</div>
            <div class="info-grid">
                <div class="info-item">
                    <div class="info-label">TC Photo Uploaded</div>
                    <div class="info-value">${tcUploaded}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Signature Photo Uploaded</div>
                    <div class="info-value">${sigUploaded}</div>
                </div>
            </div>
        </div>
        
        <div class="section">
            <div class="section-title">💰 Fee Information</div>
            <div class="amount-section">
                <div>Total Amount to be Paid</div>
                <div class="amount-value">₹${student.final_amount}</div>
            </div>
        </div>
        
        <div class="section">
            <div class="section-title">📅 Registration Details</div>
            <div class="info-grid">
                <div class="info-item">
                    <div class="info-label">Registration Date</div>
                    <div class="info-value">${regDate}</div>
                </div>
                <div class="info-item">
                    <div class="info-label">Status</div>
                    <div class="info-value">${student.status}</div>
                </div>
            </div>
        </div>
        
        <div class="footer">
            <p>This document was generated on ${new Date().toLocaleDateString('en-IN')} at ${new Date().toLocaleTimeString('en-IN')}</p>
            <p>${COLLEGE.FULL_NAME} | Registration System</p>
        </div>
    </div>
</body>
</html>`;
  };

  // Download student registration form data as readable document
  const downloadStudentData = (student) => {
    try {
      const htmlContent = getRegistrationReceiptHtml(student);
      const dataBlob = new Blob([htmlContent], { type: 'text/html' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(dataBlob);
      link.download = `${student.name}_Registration_Form_${student.registration_number}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Student registration form downloaded successfully');
    } catch (error) {
      secureLogger.error('Error downloading student data:', error);
      toast.error('Failed to download student data');
    }
  };

  // Fetch teachers from Backend API
  const fetchTeachers = useCallback(async () => {
    try {
      setLoading(true);
      secureLogger.info('🔄 Fetching teachers from Supabase...');
      
      // Fetch from Supabase
      const data = await supabaseService.getTeachers();
      
      if (data && Array.isArray(data)) {
        // Convert Supabase format to expected format
        const convertedTeachers = data.map(teacher => ({
          id: teacher.id,
          name: teacher.name,
          email: teacher.email,
          phone_number: teacher.phone_number || teacher.phone || '',
          department: teacher.department || teacher.subject || 'General',
          designation: teacher.designation || 'Faculty',
          qualification: teacher.qualification || '',
          subject: teacher.subject || '',
          joining_date: teacher.joining_date || (teacher.created_at ? new Date(teacher.created_at).toISOString().split('T')[0] : ''),
          photo_url: teacher.photo_url || '',
          photo: teacher.photo_url || '',
          image_url: teacher.photo_url || ''
        }));
        
        secureLogger.info('✅ Setting teachers from Supabase:', convertedTeachers.length);
        setTeachers(convertedTeachers);
        } else {
        secureLogger.warn('⚠️ No faculty data available from backend API.');
        setTeachers([]);
      }
      
    } catch (error) {
      secureLogger.error('❌ Error fetching teachers from backend API:', error);
      // Return empty array on error
      setTeachers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Save faculty member to Firebase
  const saveFacultyMemberToFirebase = async (facultyMember) => {
    try {
      secureLogger.info('💾 Saving faculty member to Supabase:', facultyMember);
      
      // Prepare teacher data for Supabase (match the teachers table schema)
      const teacherData = {
        id: facultyMember.id,
        name: facultyMember.name,
        email: facultyMember.email || null,
        subject: facultyMember.department || facultyMember.subject || null,
        qualification: facultyMember.qualification || null,
        photo_url: facultyMember.image_url || facultyMember.photo_url || null,
        designation: facultyMember.designation || null,
        department: facultyMember.department || null,
        phone_number: facultyMember.phone || facultyMember.phone_number || null,
        joining_date: facultyMember.joining_date || null
      };
      
      // Save to Supabase
      const result = await supabaseService.manageTeacher(teacherData);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to save teacher');
      }
      
      // Refresh teachers list
      await refreshFacultyFromFirebase();
      
      toast.success('Faculty member saved successfully');
    } catch (error) {
      secureLogger.error('Error saving faculty member:', error);
      toast.error('Failed to save faculty member');
      throw error;
    }
  };

  // Manage teacher (add/edit)
  const manageTeacher = async (teacherData) => {
    try {
      setLoading(true);
      
      secureLogger.info('🔍 Managing teacher with data:', {
        name: teacherData.get('name'),
        email: teacherData.get('email'),
        department: teacherData.get('department'),
        designation: teacherData.get('designation')
      });
      
      // Create new faculty member object
      const newFacultyMember = {
        id: teacherData.get('id') || undefined, // Let Supabase generate UUID if new
        name: teacherData.get('name'),
        email: teacherData.get('email'),
        phone: teacherData.get('phone_number') || '',
        department: teacherData.get('department') || 'General',
        designation: teacherData.get('designation') || 'Faculty',
        qualification: teacherData.get('qualification') || '',
        image_url: '', // Will be set if photo is uploaded
        created_at: new Date().toISOString()
      };
      
      // Handle photo upload (convert to data URL) or use image_url
      const photoFile = teacherData.get('photo');
      const imageUrl = teacherData.get('image_url');
      
      if (photoFile && photoFile instanceof File) {
        newFacultyMember.image_url = await convertFileToBase64(photoFile);
      } else if (imageUrl) {
        newFacultyMember.image_url = imageUrl;
      } else {
        newFacultyMember.image_url = null;
      }
      
      await saveFacultyMemberToFirebase(newFacultyMember);
      
      toast.success('Teacher saved successfully');
      await fetchTeachers();
      return { success: true };
    } catch (error) {
      secureLogger.error('Error saving teacher:', error);
      toast.error('Failed to save teacher');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // const saveFacultyMember = (newFacultyMember, currentFaculty) => {
  //   let updatedFaculty;
  //   if (newFacultyMember.id && currentFaculty.find(f => f.id === newFacultyMember.id)) {
  //     // Update existing faculty member
  //     updatedFaculty = currentFaculty.map(faculty => 
  //       faculty.id === newFacultyMember.id ? newFacultyMember : faculty
  //     );
  //   } else {
  //     // Add new faculty member
  //     updatedFaculty = [...currentFaculty, newFacultyMember];
  //   }
  //   
  //   localStorage.setItem('faculty_members', JSON.stringify(updatedFaculty));
  // };

  // Delete teacher
  const deleteTeacher = async (teacherId) => {
    try {
      // Delete from Supabase
      const result = await supabaseService.deleteTeacher(teacherId);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete teacher');
      }
      
      // Refresh teachers list
      await refreshFacultyFromFirebase();
      
      toast.success('Teacher deleted successfully');
      return { success: true };
    } catch (error) {
      secureLogger.error('Error deleting teacher:', error);
      toast.error('Failed to delete teacher');
      throw error;
    }
  };

  // Fetch gallery photos from Supabase
  const fetchGalleryPhotos = useCallback(async () => {
    try {
      setLoading(true);
      secureLogger.info('🔄 Fetching gallery photos from Supabase...');
      
      // Fetch from Supabase
      const data = await supabaseService.getGalleryPhotos();
      
      if (data && Array.isArray(data)) {
        secureLogger.info('✅ Setting gallery photos from Supabase:', data.length);
        setGalleryPhotos(data);
      } else {
        secureLogger.warn('⚠️ No gallery photos available from Supabase.');
        setGalleryPhotos([]);
      }
      
    } catch (error) {
      secureLogger.error('❌ Error fetching gallery photos from Supabase:', error);
      // Return empty array on error
      setGalleryPhotos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Upload gallery photo
  const uploadGalleryPhoto = async (photoData) => {
    try {
      setLoading(true);
      
      // Get current gallery photos from Supabase
      const currentPhotos = await supabaseService.getGalleryPhotos();
      
      // Check if this is an edit operation
      const photoId = photoData.get('id');
      const isEdit = photoId && photoId !== '';
      
      secureLogger.info('🔍 Gallery photo operation:', { photoId, isEdit, title: photoData.get('title') });
      
      let updatedPhotos;
      
      if (isEdit) {
        // Edit existing photo
        secureLogger.info('🔄 Editing existing photo with ID:', photoId);
        updatedPhotos = await Promise.all(currentPhotos.map(async photo => {
          if (photo.id === photoId) {
            const updatedPhoto = {
              ...photo,
              title: photoData.get('title') || photo.title,
              description: photoData.get('description') || photo.description,
            };
            
            // Handle photo upload (convert to data URL)
            const photoFile = photoData.get('photo');
            if (photoFile && photoFile instanceof File) {
              updatedPhoto.image_url = await convertFileToBase64(photoFile);
            } else if (photoData.get('image_url')) {
              updatedPhoto.image_url = photoData.get('image_url');
            }
            
            return updatedPhoto;
          }
          return photo;
        }));
        
        toast.success('Photo updated successfully');
      } else {
        // Create new photo
        secureLogger.info('➕ Creating new photo');
        const newPhoto = {
          id: Date.now().toString(),
          title: photoData.get('title') || 'Gallery Photo',
          description: photoData.get('description') || '',
          image_url: '', // Will be set if photo is uploaded
          created_at: new Date().toISOString()
        };
        
        // Handle photo upload (convert to data URL)
        const photoFile = photoData.get('photo');
        if (photoFile && photoFile instanceof File) {
          newPhoto.image_url = await convertFileToBase64(photoFile);
        } else if (photoData.get('image_url')) {
          newPhoto.image_url = photoData.get('image_url');
        }
        
        updatedPhotos = [...currentPhotos, newPhoto];
        toast.success('Photo uploaded successfully');
      }
      
      // Save to Supabase
      if (isEdit) {
        // Update existing photo
        const photoToUpdate = updatedPhotos.find(p => p.id === photoId);
        if (photoToUpdate) {
          const result = await supabaseService.manageGalleryPhoto(photoToUpdate);
          if (!result.success) {
            throw new Error(result.error || 'Failed to update photo');
          }
        }
      } else {
        // Add new photo
        const newPhoto = updatedPhotos[updatedPhotos.length - 1];
        const result = await supabaseService.manageGalleryPhoto(newPhoto);
        if (!result.success) {
          throw new Error(result.error || 'Failed to upload photo');
        }
      }
      
      // Refresh gallery photos
      await refreshGalleryPhotosFromFirebase();
      
      return { success: true };
    } catch (error) {
      secureLogger.error('Error uploading gallery photo:', error);
      toast.error('Failed to upload photo');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete gallery photo
  const deleteGalleryPhoto = async (photoId) => {
    try {
      secureLogger.info('🗑️ Deleting gallery photo with ID:', photoId, typeof photoId);
      
      // Get current gallery photos from Supabase
      const currentPhotos = await supabaseService.getGalleryPhotos();
      secureLogger.info('📸 Current photos before deletion:', currentPhotos.length, currentPhotos.map(p => ({ id: p.id, title: p.title })));
      
      // Remove the photo (handle both string and number IDs)
      const updatedPhotos = currentPhotos.filter(photo => 
        photo.id !== photoId && 
        photo.id !== photoId.toString() && 
        photo.id !== parseInt(photoId)
      );
      
      secureLogger.info('📸 Photos after deletion:', updatedPhotos.length, updatedPhotos.map(p => ({ id: p.id, title: p.title })));
      
      if (updatedPhotos.length === currentPhotos.length) {
        secureLogger.error('❌ Photo not found for deletion. Photo ID:', photoId);
        toast.error('Photo not found');
        return { success: false, error: 'Photo not found' };
      }
      
      // Delete from Supabase
      const result = await supabaseService.deleteGalleryPhoto(photoId);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete photo');
      }
      
      // Refresh gallery photos
      await refreshGalleryPhotosFromFirebase();
      
      toast.success('Photo deleted successfully');
      return { success: true };
    } catch (error) {
      secureLogger.error('❌ Error deleting gallery photo:', error);
      toast.error('Failed to delete photo');
      return { success: false, error: error.message };
    }
  };

  // Process UPI payment
  const processUPIPayment = async (paymentData) => {
    try {
      // SECURITY: axios calls disabled - use secure API service instead
      // const response = await axios.post(API_ENDPOINTS.PROCESS_UPI_PAYMENT, paymentData);
      throw new Error('UPI payment processing disabled for security');
    } catch (error) {
      const message = error.response?.data?.error || 'Payment failed';
      toast.error(message);
      throw error;
    }
  };

  // Create PayPal order
  const createPayPalOrder = async (cart) => {
    try {
      // SECURITY: axios calls disabled - use secure API service instead
      // const response = await axios.post(API_ENDPOINTS.PAYPAL_ORDERS, { cart });
      throw new Error('PayPal order creation disabled for security');
    } catch (error) {
      secureLogger.error('Error creating PayPal order:', error);
      toast.error('Failed to create payment order');
      throw error;
    }
  };

  // Capture PayPal order
  const capturePayPalOrder = async (orderID) => {
    try {
      // SECURITY: axios calls disabled - use secure API service instead
      // const response = await axios.post(`${API_ENDPOINTS.PAYPAL_CAPTURE}/${orderID}/capture`);
      throw new Error('PayPal capture disabled for security');
    } catch (error) {
      secureLogger.error('Error capturing PayPal order:', error);
      toast.error('Failed to capture payment');
      throw error;
    }
  };

  // Create student PayPal order
  const createStudentPayPalOrder = useCallback(async (studentData) => {
    try {
      // SECURITY: axios calls disabled - use secure API service instead
      // const response = await axios.post(API_ENDPOINTS.STUDENT_PAYPAL_ORDERS, studentData);
      throw new Error('Student PayPal order creation disabled for security');
    } catch (error) {
      secureLogger.error('Error creating student PayPal order:', error);
      toast.error('Failed to create payment order');
      throw error;
    }
  }, []);

  // Get global student data
  // const getGlobalStudents = () => {
  //   return retrieveGlobalData(GLOBAL_STUDENTS_KEY, []);
  // };

  // Get global payment data
  // const getGlobalPayments = () => {
  //   return retrieveGlobalData(GLOBAL_PAYMENTS_KEY, []);
  // };

  // Get sync URL for data sharing
  const getDataSyncUrl = (key) => {
    return localStorage.getItem(`${key}_sync_url`) || null;
  };

  // Sync data from legacy localStorage to global storage
  const syncLegacyData = useCallback(() => {
    try {
      // Sync students
      const legacyStudents = JSON.parse(localStorage.getItem('students') || '[]');
      if (legacyStudents.length > 0) {
        const globalStudents = retrieveGlobalData(GLOBAL_STUDENTS_KEY, []);
        const mergedStudents = [...globalStudents];
        
        // Add legacy students that don't exist in global storage
        legacyStudents.forEach(legacyStudent => {
          const exists = mergedStudents.find(global => global.id === legacyStudent.id);
          if (!exists) {
            mergedStudents.push({
              ...legacyStudent,
              device_info: {
                userAgent: 'Legacy Data',
                timestamp: Date.now(),
                location: 'Legacy Sync'
              }
            });
          }
        });
        
        storeGlobalData(GLOBAL_STUDENTS_KEY, mergedStudents);
      }

      // Sync payments
      const legacyPayments = JSON.parse(localStorage.getItem('payments') || '[]');
      if (legacyPayments.length > 0) {
        const globalPayments = retrieveGlobalData(GLOBAL_PAYMENTS_KEY, []);
        const mergedPayments = [...globalPayments];
        
        // Add legacy payments that don't exist in global storage
        legacyPayments.forEach(legacyPayment => {
          const exists = mergedPayments.find(global => global.id === legacyPayment.id);
          if (!exists) {
            mergedPayments.push({
              ...legacyPayment,
              device_info: {
                userAgent: 'Legacy Data',
                timestamp: Date.now(),
                location: 'Legacy Sync'
              }
            });
          }
        });
        
        storeGlobalData(GLOBAL_PAYMENTS_KEY, mergedPayments);
      }
    } catch (error) {
      secureLogger.error('Error syncing legacy data:', error);
    }
  }, []);

  // Initialize global data sync on component mount
  React.useEffect(() => {
    syncLegacyData();
  }, [syncLegacyData]);


  const value = {
    subjects,
    minorSubjects,
    teachers,
    galleryPhotos,
    students,
    payments,
    loading,
    fetchSubjects,
    calculateFee,
    processRegistration,
    fetchTeachers,
    manageTeacher,
    deleteTeacher,
    fetchGalleryPhotos,
    uploadGalleryPhoto,
    deleteGalleryPhoto,
    processUPIPayment,
    createPayPalOrder,
    capturePayPalOrder,
    createStudentPayPalOrder,
    addSubject,
    updateSubject,
    deleteSubject,
    addMinorSubject,
    updateMinorSubject,
    deleteMinorSubject,
    getMinorSubjects,
    fetchMinorSubjects,
    cleanDuplicateSubjects,
    cleanDuplicateGalleryPhotos,
    refreshSubjectsFromFirebase,
    refreshGalleryPhotosFromFirebase,
    refreshFacultyFromFirebase,
    updatePaymentStatus,
    updateStudentPaymentStatus,
    getGlobalStudents: () => students,
    getGlobalPayments: () => payments,
    getDataSyncUrl,
    syncLegacyData,
    deleteStudent,
    updateStudent,
    downloadStudentFile,
    downloadStudentData,
    getRegistrationReceiptHtml,
    // Degree Fee Structure Management
    getDegreeFeeStructures,
    addDegreeFeeStructure,
    updateDegreeFeeStructure,
    deleteDegreeFeeStructure,
    // Caste Gender Discount Management
    getCasteGenderDiscounts,
    addCasteGenderDiscount,
    updateCasteGenderDiscount,
    deleteCasteGenderDiscount,
    // Firebase Data Management
    // Firebase functions removed - using Supabase now
    clearAllFirebaseData: () => { console.warn('Firebase functions removed - use Supabase admin panel'); },
    initializeFreshData: () => { console.warn('Firebase functions removed - use Supabase admin panel'); }
  };

  return (
    <StudentContext.Provider value={value}>
      {children}
    </StudentContext.Provider>
  );
};
